/**
 * Fuzzy Search Utility
 * 
 * Implementa busca aproximada usando Levenshtein Distance com normalização
 * de texto (remoção de acentos, case-insensitive).
 */

/**
 * Remove acentos e caracteres especiais de uma string
 */
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacríticos
    .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
    .trim();
}

/**
 * Calcula a distância de Levenshtein entre duas strings
 * (número mínimo de edições para transformar uma string em outra)
 */
export function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  // Inicializa primeira coluna
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  // Inicializa primeira linha
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  // Preenche a matriz
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substituição
          matrix[i][j - 1] + 1,     // inserção
          matrix[i - 1][j] + 1      // remoção
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Calcula a similaridade entre duas strings (0 a 1)
 * 1 = idênticas, 0 = completamente diferentes
 */
export function similarity(a: string, b: string): number {
  const normalizedA = normalizeText(a);
  const normalizedB = normalizeText(b);

  if (normalizedA === normalizedB) return 1;
  if (normalizedA.length === 0 || normalizedB.length === 0) return 0;

  const distance = levenshteinDistance(normalizedA, normalizedB);
  const maxLength = Math.max(normalizedA.length, normalizedB.length);

  return 1 - distance / maxLength;
}

/**
 * Verifica se a query está contida parcialmente no texto
 */
export function containsPartial(text: string, query: string): boolean {
  const normalizedText = normalizeText(text);
  const normalizedQuery = normalizeText(query);

  return normalizedText.includes(normalizedQuery);
}

/**
 * Verifica se alguma palavra da query tem match aproximado com palavras do texto
 */
export function hasWordMatch(text: string, query: string, threshold: number = 0.7): boolean {
  const textWords = normalizeText(text).split(/\s+/).filter(w => w.length > 2);
  const queryWords = normalizeText(query).split(/\s+/).filter(w => w.length > 2);

  return queryWords.some(qWord =>
    textWords.some(tWord => similarity(qWord, tWord) >= threshold)
  );
}

export interface FuzzyMatchResult<T> {
  item: T;
  score: number;
  matchedField: string;
  matchedValue: string;
}

export interface FuzzySearchOptions {
  /** Threshold mínimo de similaridade (0-1). Default: 0.5 */
  threshold?: number;
  /** Campos para buscar (getter functions) */
  fields: Array<{
    name: string;
    getValue: (item: any) => string | string[];
    weight?: number;
  }>;
}

/**
 * Realiza busca fuzzy em uma lista de itens
 * 
 * @param items Lista de itens para buscar
 * @param query Termo de busca
 * @param options Opções de configuração
 * @returns Lista de resultados ordenados por relevância
 */
export function fuzzySearch<T>(
  items: T[],
  query: string,
  options: FuzzySearchOptions
): FuzzyMatchResult<T>[] {
  const { threshold = 0.5, fields } = options;
  const normalizedQuery = normalizeText(query);

  if (!normalizedQuery) {
    return items.map(item => ({
      item,
      score: 1,
      matchedField: '',
      matchedValue: ''
    }));
  }

  const results: FuzzyMatchResult<T>[] = [];

  for (const item of items) {
    let bestScore = 0;
    let bestField = '';
    let bestValue = '';

    for (const field of fields) {
      const weight = field.weight ?? 1;
      const rawValue = field.getValue(item);
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];

      for (const value of values) {
        if (!value) continue;

        // Primeiro: verificar match exato ou parcial (prioridade máxima)
        if (containsPartial(value, query)) {
          const score = 0.95 * weight;
          if (score > bestScore) {
            bestScore = score;
            bestField = field.name;
            bestValue = value;
          }
        }

        // Segundo: verificar match por palavras (exige 85% de similaridade)
        if (hasWordMatch(value, query, 0.85)) {
          const score = 0.85 * weight;
          if (score > bestScore) {
            bestScore = score;
            bestField = field.name;
            bestValue = value;
          }
        }

        // Terceiro: verificar similaridade geral (só aceita se muito similar)
        const sim = similarity(value, query) * weight;
        // Só aceita similaridade geral se for >= 70%
        if (sim >= 0.7 && sim > bestScore) {
          bestScore = sim;
          bestField = field.name;
          bestValue = value;
        }

        // Quarto: verificar cada palavra individualmente (exige alta similaridade)
        const words = value.split(/\s+/).filter(w => w.length >= 3);
        for (const word of words) {
          const wordSim = similarity(word, query);
          // Só aceita se a palavra for muito similar (>= 80%)
          if (wordSim >= 0.8) {
            const finalScore = wordSim * weight * 0.9;
            if (finalScore > bestScore) {
              bestScore = finalScore;
              bestField = field.name;
              bestValue = value;
            }
          }
        }
      }
    }

    if (bestScore >= threshold) {
      results.push({
        item,
        score: bestScore,
        matchedField: bestField,
        matchedValue: bestValue
      });
    }
  }

  // Ordena por score (maior primeiro)
  return results.sort((a, b) => b.score - a.score);
}

/**
 * Encontra a melhor sugestão de correção para um termo de busca
 * 
 * @param query Termo digitado pelo usuário
 * @param candidates Lista de termos candidatos (nomes de coleções, tags, etc)
 * @param threshold Threshold mínimo de similaridade
 * @returns A melhor sugestão ou null
 */
export function findBestSuggestion(
  query: string,
  candidates: string[],
  threshold: number = 0.5
): { suggestion: string; score: number } | null {
  const normalizedQuery = normalizeText(query);
  
  if (!normalizedQuery) return null;

  let bestSuggestion: string | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    // Pular se for exatamente igual (não precisa sugerir)
    if (normalizeText(candidate) === normalizedQuery) {
      return null;
    }

    const score = similarity(candidate, query);
    
    if (score > bestScore && score >= threshold && score < 1) {
      bestScore = score;
      bestSuggestion = candidate;
    }
  }

  if (bestSuggestion && bestScore >= threshold) {
    return { suggestion: bestSuggestion, score: bestScore };
  }

  return null;
}
